import { useRef, useState } from 'react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { User, Mail, Shield, Calendar, Camera, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import type { User as UserType } from '@/types'
import { useAuthStore } from '@/stores/authStore'
import { authService } from '@/services/authService'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { cn } from '@/utils/cn'
import { ProfilePasswordFields } from '@/components/Account/ProfilePasswordFields'
import { changePasswordClientError } from '@/components/Account/profileAccount'

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

const AVATAR_ACCEPT = 'image/png,image/jpeg,image/webp'
const AVATAR_MAX_BYTES = 2 * 1024 * 1024

interface AccountTabProps {
  user: UserType | null
}

export function AccountTab({ user }: AccountTabProps) {
  const setUser = useAuthStore((s) => s.setUser)
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [name, setName] = useState(user?.name ?? '')
  const [savingName, setSavingName] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)

  if (!user) return null

  const avatarSrc = user.avatarUrl ? resolvePublicAssetUrl(user.avatarUrl) : ''
  const showPhoto = Boolean(avatarSrc) && !imgFailed

  async function handleFile(file: File) {
    if (!AVATAR_ACCEPT.split(',').includes(file.type)) {
      toast.error('Formato inválido. Use PNG, JPEG ou WebP.')
      return
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast.error('Imagem muito grande. Máximo 2 MB.')
      return
    }
    setUploading(true)
    try {
      const updated = await authService.uploadAvatar(file)
      setUser(updated)
      setImgFailed(false)
      toast.success('Foto de perfil atualizada')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao enviar foto'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function handleRemove() {
    setUploading(true)
    try {
      const updated = await authService.removeAvatar()
      setUser(updated)
      setImgFailed(false)
      toast.success('Foto removida')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao remover foto'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className='glass-card space-y-5'>
      <h3 className='font-semibold text-[var(--font-color-h3)]' style={{ fontSize: 'var(--font-size-h3)' }}>
        Minha Conta
      </h3>

      <div className='flex items-center gap-4'>
        <div className='relative shrink-0'>
          <button
            type='button'
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className={cn(
              'group relative w-14 h-14 rounded-full overflow-hidden flex items-center justify-center',
              'bg-[var(--brand-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
              uploading && 'opacity-70 cursor-wait',
            )}
            aria-label={showPhoto ? 'Trocar foto de perfil' : 'Enviar foto de perfil'}
            title={showPhoto ? 'Trocar foto' : 'Enviar foto'}
          >
            {showPhoto ? (
              <img
                src={avatarSrc}
                alt={user.name}
                className='h-full w-full object-cover'
                onError={() => setImgFailed(true)}
              />
            ) : (
              <span className='text-white font-bold text-lg'>{getInitials(user.name)}</span>
            )}
            <span className='absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100'>
              {uploading ? (
                <Loader2 size={16} className='text-white animate-spin' />
              ) : (
                <Camera size={16} className='text-white' />
              )}
            </span>
          </button>
          <input
            ref={fileRef}
            type='file'
            accept={AVATAR_ACCEPT}
            className='hidden'
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void handleFile(file)
            }}
          />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='font-semibold text-[var(--text-primary)]'>{user.name}</p>
          <p className='text-[var(--text-muted)] text-[13px]'>{user.email}</p>
          <div className='mt-2 flex flex-wrap gap-2'>
            <Button
              variant='secondary'
              size='sm'
              disabled={uploading}
              icon={uploading ? <Loader2 size={13} className='animate-spin' /> : <Camera size={13} />}
              onClick={() => fileRef.current?.click()}
            >
              {showPhoto ? 'Trocar foto' : 'Enviar foto'}
            </Button>
            {showPhoto && (
              <Button
                variant='ghost'
                size='sm'
                disabled={uploading}
                icon={<Trash2 size={13} />}
                onClick={() => void handleRemove()}
              >
                Remover
              </Button>
            )}
          </div>
          <p className='mt-1.5 text-[10px] text-[var(--text-muted)]'>PNG, JPEG ou WebP · máx. 2 MB</p>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <div className='flex flex-col gap-1.5'>
          <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
            Nome
          </label>
          <div className='relative'>
            <span className='absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]'>
              <User size={14} />
            </span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className='w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md pl-9 pr-3 py-2 text-[13px] min-h-[44px]'
            />
          </div>
        </div>

        <div className='flex flex-col gap-1.5'>
          <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
            Email
          </label>
          <div className='relative'>
            <span className='absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]'>
              <Mail size={14} />
            </span>
            <input
              value={user.email}
              disabled
              className='w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md pl-9 pr-3 py-2 text-[13px] opacity-70 cursor-not-allowed min-h-[44px]'
            />
          </div>
        </div>

        <div className='flex flex-col gap-1.5'>
          <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
            Role
          </label>
          <div className='flex items-center min-h-[44px]'>
            <Badge variant='default'>
              <Shield size={11} className='mr-1' />
              {user.role}
            </Badge>
          </div>
        </div>

        <div className='flex flex-col gap-1.5'>
          <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
            Status
          </label>
          <div className='flex items-center min-h-[44px]'>
            <Badge variant={user.active ? 'success' : 'muted'}>
              {user.active ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>
        </div>

        <div className='flex flex-col gap-1.5 sm:col-span-2'>
          <label className='font-medium text-[var(--font-color-label)] uppercase tracking-wider' style={{ fontSize: 'var(--font-size-label)' }}>
            Membro desde
          </label>
          <div className='relative'>
            <span className='absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]'>
              <Calendar size={14} />
            </span>
            <input
              value={formatDate(user.createdAt)}
              disabled
              className='w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] rounded-md pl-9 pr-3 py-2 text-[13px] opacity-70 cursor-not-allowed min-h-[44px]'
            />
          </div>
        </div>
      </div>

      <div className='pt-2 flex flex-wrap items-start gap-3'>
        <Button
          variant='primary'
          size='sm'
          loading={savingName}
          disabled={name.trim() === user.name}
          onClick={async () => {
            const nextName = name.trim()
            if (nextName.length < 2) {
              toast.error('Nome deve ter pelo menos 2 caracteres')
              return
            }
            setSavingName(true)
            try {
              setUser(await authService.updateName(nextName))
              toast.success('Nome atualizado')
            } catch (err) {
              toast.error(getFriendlyError(err, 'Não foi possível salvar o nome'))
            } finally {
              setSavingName(false)
            }
          }}
        >
          Salvar nome
        </Button>
        <ProfilePasswordFields
          saving={savingPassword}
          onSubmit={async (input) => {
            const message = changePasswordClientError(input)
            if (message) {
              toast.error(message)
              return false
            }
            setSavingPassword(true)
            try {
              await authService.changePassword({
                currentPassword: input.currentPassword,
                newPassword: input.newPassword,
              })
              toast.success('Senha alterada')
              return true
            } catch (err) {
              toast.error(getFriendlyError(err, 'Não foi possível alterar a senha'))
              return false
            } finally {
              setSavingPassword(false)
            }
          }}
        />
      </div>
    </div>
  )
}
